import {
  // Box,
  DrawerHeader,
  DrawerBody,
  Stack,
  Image,
  Text,
  RadioGroup,
  Radio,
  Link,
  Divider,
  SimpleGrid,
  InputGroup,
  Input,
  InputRightElement,
  InputLeftElement,
  Icon,
  useBoolean,
  useClipboard,
  IconButton,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconArrowLeft, IconClip } from 'assets/icons';
import { calendarDay, googleCalendar, yahoo } from 'assets/images';
import Button from 'components/Button';
import EmployeesAndGroups from 'containers/Scheduling/RightDrawer/FormikEmployeesAndGroups';
import { useFetchScheduleSubscribeUrls } from 'hooks/scheduling/useFetchScheduleSubscribeUrls';
import { useGenerateSubscribeUrl } from 'hooks/scheduling/useGenerateSubscribeUrl';

interface ISubscibeCalendarProps {
  onClose: () => void;
}

const SubscribeCalendar: React.FC<ISubscibeCalendarProps> = ({ onClose }) => {
  const { t } = useTranslation(['scheduling', 'common']);

  const [checkedRadio, setCheckedRadio] = useState<'all_user' | 'my_user' | 'specific_user' | ''>('');

  const [flag, setFlag] = useBoolean();

  const { data: subscribeUrls } = useFetchScheduleSubscribeUrls();
  const { status, mutateAsync, data: specificUsersUrl } = useGenerateSubscribeUrl();

  const handleRadioGroupChange = (value: any) => {
    setCheckedRadio(value);
  };

  const getCheckedRadioLink = () => {
    let selectedCheckedRadio = '';

    switch (checkedRadio) {
      case 'all_user':
        selectedCheckedRadio = subscribeUrls?.payload?.all_users;
        break;

      case 'my_user':
        selectedCheckedRadio = subscribeUrls?.payload?.user_schedule;
        break;

      case 'specific_user':
        selectedCheckedRadio = specificUsersUrl?.payload?.subscribe_url;
        break;

      default:
        selectedCheckedRadio = '';
    }

    return selectedCheckedRadio;
  };

  const { onCopy, hasCopied } = useClipboard(getCheckedRadioLink());

  const handleGenerateUrlsButtonClick = (values: any) => {
    const postData = {
      user_ids: values.user_ids.map((item: any) => item.userId),
    };

    mutateAsync(postData);
  };

  return (
    <Formik enableReinitialize initialValues={{ user_ids: [], group_ids: [] }} onSubmit={handleGenerateUrlsButtonClick}>
      {({ values }) => (
        <Form noValidate autoComplete="off">
          {flag ? (
            <>
              <DrawerHeader>
                <IconButton
                  aria-label="back"
                  icon={<Icon as={IconArrowLeft} />}
                  variant="ghost"
                  p="1"
                  cursor="pointer"
                  size="sm"
                  mr="2"
                  sx={{ '& path': { fill: 'black' } }}
                  onClick={setFlag.off}
                />
                {t('common:selectUsers')}
              </DrawerHeader>
              <DrawerBody>
                <EmployeesAndGroups />
              </DrawerBody>
            </>
          ) : (
            <>
              <DrawerHeader>{t('scheduling:subscribeToCalendar')}</DrawerHeader>
              <DrawerBody>
                <Stack direction="row" alignItems="center" spacing="4" mb="6">
                  <Image src={googleCalendar} alt="google calendar" />
                  <Image src={calendarDay} alt="google calendar" />
                  <Image src={yahoo} alt="google calendar" />
                </Stack>
                <Text mb="4">{t('scheduling:generateLinkToAddSchedulesTitle')}</Text>
                <Text mb="10">
                  {t('scheduling:forFutherInstructionsOnConnectingYourSchedules')},{' '}
                  <Link
                    color="primary.900"
                    textDecoration="underline"
                    href="https://help.timeero.com/scheduling"
                    isExternal
                  >
                    {t('scheduling:pleaseVisitHere')}.
                  </Link>
                </Text>
                <Text textTransform="uppercase" color="gray.500" mb="2" fontSize="xs">
                  {t('scheduling:generateLinkFor')}:{' '}
                </Text>
                <RadioGroup onChange={handleRadioGroupChange} value={checkedRadio} size="md">
                  <Stack direction="row" spacing="3" justifyContent="space-between">
                    <Radio value="all_user">{t('scheduling:allUsers')}</Radio>
                    <Radio value="my_user">{t('scheduling:myUser')}</Radio>
                    <Radio value="specific_user">{t('scheduling:specificUsers')}</Radio>
                  </Stack>
                </RadioGroup>
                <Divider my="4" />
                {checkedRadio === 'specific_user' ? (
                  <>
                    <Text color="gray.500" fontSize="12px" fontWeight="600" mb="1" textTransform="uppercase">
                      {t('scheduling:selectSpecificUsers')}
                    </Text>
                    <InputGroup size="lg" mb="6">
                      <Input
                        fontSize="sm"
                        variant="filled"
                        type="text"
                        border="1px"
                        borderColor="inherit"
                        onChange={() => null}
                        value={`${values.user_ids.length || t('common:all')} ${t('common:users')}`}
                      />
                      <InputRightElement
                        width="auto"
                        mr="2"
                        children={
                          <Button size="sm" variant="link" onClick={setFlag.on}>
                            {values.user_ids.length ? t('common:edit') : t('common:add')}
                          </Button>
                        }
                      />
                    </InputGroup>
                    {specificUsersUrl?.payload ? (
                      <InputGroup size="lg">
                        <InputLeftElement
                          pointerEvents="none"
                          children={<Icon as={IconClip} cursor="pointer" boxSize="4" />}
                          width="10"
                        />
                        <Input
                          pl="10"
                          isReadOnly
                          type="tel"
                          placeholder={t('common:phoneNumber')}
                          border="1px dashed"
                          borderColor="gray.200"
                          _focus={{ borderColor: 'gray.400' }}
                          fontSize="sm"
                          color="primary.500"
                          bg="gray.100"
                          onChange={() => null}
                          value={getCheckedRadioLink()}
                        />
                      </InputGroup>
                    ) : null}
                    <SimpleGrid columns={2} spacing="4" mt="4">
                      <Button colorScheme="gray" size="lg" onClick={onClose}>
                        {t('common:cancel')}
                      </Button>
                      {specificUsersUrl?.payload ? (
                        <Button colorScheme="secondary" size="lg" onClick={onCopy}>
                          {hasCopied ? t('scheduling:copied')! : t('scheduling:copyLink')}
                        </Button>
                      ) : (
                        <Button
                          colorScheme="secondary"
                          size="lg"
                          type="submit"
                          isLoading={status === 'loading'}
                          isDisabled={values.user_ids.length ? false : true}
                        >
                          {t('scheduling:generateLink')}
                        </Button>
                      )}
                    </SimpleGrid>
                  </>
                ) : (
                  <>
                    <InputGroup size="lg">
                      <InputLeftElement
                        pointerEvents="none"
                        children={<Icon as={IconClip} cursor="pointer" boxSize="4" />}
                        width="10"
                      />
                      <Input
                        pl="10"
                        isReadOnly
                        type="tel"
                        placeholder={t('scheduling:subscriptionURL')}
                        border="1px dashed"
                        borderColor="gray.200"
                        _focus={{ borderColor: 'gray.400' }}
                        fontSize="sm"
                        color="primary.500"
                        bg="gray.100"
                        onChange={() => null}
                        value={getCheckedRadioLink()}
                      />
                    </InputGroup>
                    <SimpleGrid columns={2} spacing="4" mt="4">
                      <Button colorScheme="gray" size="lg" onClick={onClose}>
                        {t('common:cancel')}
                      </Button>
                      <Button colorScheme="secondary" size="lg" onClick={onCopy}>
                        {hasCopied ? t('scheduling:copied')! : t('scheduling:copyLink')}
                      </Button>
                    </SimpleGrid>{' '}
                  </>
                )}
              </DrawerBody>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default SubscribeCalendar;
